import React from 'react';
import { Button, Skeleton, Tooltip } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import type { DeliverableListItemDto } from 'components/DealDeliverables';
import { ServiceTypeIcon } from 'components/DealDeliverables/serviceTypeIcons';
import { FormatChip, formatCreatedAt, opensInModal } from 'components/DealDeliverables/deliverablePresentation';

/**
 * Rendered height of one `ReportRow`, in px, and the single source of truth for the scroll
 * container's max height. The row is pinned to this height and every line box inside it has an
 * explicit `leading-*`, so the constant cannot drift from what the browser lays out:
 * 10px padding + 20px title + 3px gap + 22px metadata line (the antd Tag's own height) + 10px
 * padding. Tailwind arbitrary font sizes such as `text-[14px]` set font-size ONLY, so without
 * explicit line heights a row measured ~61px against an assumed 54px — which is what made the
 * list scroll at exactly `maxRows` and clip its last row mid-way.
 */
export const ROW_HEIGHT_PX = 65;

/** The scroll container's own vertical padding (`py-[6px]`), added on top of the rows' share. */
export const LIST_PADDING_Y_PX = 12;

/**
 * Geometry + centring for the row's circular download button, shared by its enabled and disabled
 * variants so the two can never differ in size or shape. `!` throughout because antd's stylesheet
 * loads after Tailwind's (see the enabled button's comment) and `.ant-btn`/`.ant-btn-icon-only`
 * would otherwise win the size and radius on a specificity tie.
 */
const DOWNLOAD_BUTTON_CLASSES =
  '!flex items-center justify-center !w-[28px] !h-[28px] !min-w-[28px] !p-0 !rounded-full flex-none';

export interface CalendarReportsListProps {
  /** The deal's deliverable rows, lazy-fetched by the popover host (Task 4). */
  reports: DeliverableListItemDto[];
  /** True while the host is fetching `reports`; renders a skeleton instead of rows. */
  loading: boolean;
  /** Invoked with the clicked report when its Open button is clicked. */
  onOpen: (deliverable: DeliverableListItemDto) => void;
  /**
   * Overrides the "{n} Report(s)" header count. When omitted the header derives the count
   * from `reports.length`. The host supplies it while `loading` is true — `reports` is empty
   * then, but the calendar payload's `deliverablesCount` already knows how many rows are
   * coming, so the header can be correct from the first frame. Deliberately a plain number
   * rather than placeholder `reports` entries: the header must never depend on this component
   * happening to check `loading` before it iterates `reports`.
   */
  headerCount?: number;
  /** Rows visible before the list scrolls; drives the scroll container's max height. Defaults to 3. */
  maxRows?: number;
}

interface ReportRowProps {
  /** The deliverable to render as a row. */
  report: DeliverableListItemDto;
  /** Invoked with `report` when its Open button is clicked. */
  onOpen: (deliverable: DeliverableListItemDto) => void;
}

/**
 * One deliverable row in the reports popover: service-type icon tile, title, a metadata line
 * carrying the file format followed by "{type} · {date}", and a download control.
 *
 * The TITLE is the open affordance — rendered as a blue text button when the viewer can display
 * the file (`opensInModal`), and as plain dark text when it cannot, so the row never offers a
 * link that would go nowhere. That is why there is no separate "Open" button: one row, one
 * primary action, and the blue explains itself without a second control competing beside it.
 * Download stays a distinct icon button because it is a different outcome (a file on disk, not a
 * view), and it is offered for every format including the ones the viewer cannot render.
 *
 * The format chip leads the metadata line, reusing the same `FormatChip`, colour map, and
 * `MMM DD, YYYY` date helper as the deal Deliverables panel so one file reads identically on both
 * surfaces. The row's `title` repeats the metadata because that line can truncate at 388px for a
 * long service-type label.
 * Reads `report.documents[0]` directly behind a length guard rather than calling the
 * shared `getPrimaryDocument` helper — that helper asserts a populated `documents`
 * array, and an empty array is a required, non-crashing case for this row. Do not
 * "fix" this back to `getPrimaryDocument`; it would reintroduce the crash.
 * @param {ReportRowProps} props - The report and its open handler.
 * @returns {React.ReactElement} The rendered row.
 */
function ReportRow({ report, onOpen }: ReportRowProps): React.ReactElement {
  const doc = report.documents.length > 0 ? report.documents[0] : undefined;
  // Both `format` and `fileName` go to the shared predicate: an `.eml` is recognisable only from
  // the name (the backend derives no format limb for it), so passing format alone would leave an
  // email deliverable inert here while it opens on the deal panel.
  const canOpen = doc != null && opensInModal(doc.format, doc.fileName);
  const titleClasses = 'text-[14px] leading-[20px] font-bold truncate';

  return (
    // `h-[65px]` must stay equal to ROW_HEIGHT_PX — Tailwind's JIT cannot read the constant, so a
    // test asserts the two agree rather than leaving them to drift. `box-border` keeps the hairline
    // divider inside that height instead of adding a 1px drift to it.
    <div
      className="flex items-center gap-[12px] px-[16px] h-[65px] box-border hover:bg-[#F7F9FC] [border-top:1px_solid_#EEF1F5] first:[border-top:none]"
      title={`${report.serviceTypeLabel} · ${formatCreatedAt(report.createdAt)}`}
      data-report-row
    >
      {/*
       * 45x45 is the tallest thing in the row, tying the text column's 45px (20px title + 3px gap
       * + 22px metadata line) — so it sits inside ROW_HEIGHT_PX without changing it.
       */}
      <div className="flex items-center justify-center w-[45px] h-[45px] rounded-[11px] bg-[#ECEFF1] flex-none">
        <ServiceTypeIcon code={report.serviceTypeCode} className="text-[25px] text-[#455876]" />
      </div>
      <div className="min-w-0 flex-1">
        {canOpen ? (
          <button
            type="button"
            onClick={() => onOpen(report)}
            className={`${titleClasses} block max-w-full p-0 bg-transparent border-none cursor-pointer text-left text-[#1890FF] hover:text-[#0F6FD1]`}
          >
            {report.title}
          </button>
        ) : (
          <div className={`${titleClasses} text-[#222]`}>{report.title}</div>
        )}
        <div className="flex items-center gap-[6px] mt-[3px] h-[22px]">
          {doc && <FormatChip format={doc.format} />}
          <span className="text-[12px] leading-[22px] text-[#777] truncate">
            {report.serviceTypeLabel} · {formatCreatedAt(report.createdAt)}
          </span>
        </div>
      </div>
      {doc &&
        (doc.downloadUrl ? (
          <Button
            shape="circle"
            icon={<DownloadOutlined />}
            aria-label="Download"
            // Every visual utility here needs `!`. `MainLayout` imports the antd theme with a
            // dynamic `await import('AppVantage.scss')`, so antd's stylesheet is appended AFTER
            // Tailwind's — and `.ant-btn` ties Tailwind's single-class specificity, so on a tie the
            // later sheet wins. Without `!important`, `.ant-btn`'s own `color` and `border` beat
            // `text-white`/`border-none` and the glyph renders dark-on-navy, i.e. invisible.
            className={`${DOWNLOAD_BUTTON_CLASSES} !border-none !bg-[#152D51] !text-white hover:!bg-[#1c3a67] hover:!text-white focus:!bg-[#1c3a67] focus:!text-white`}
            onClick={() => window.open(doc.downloadUrl, '_blank', 'noopener,noreferrer')}
          />
        ) : (
          <Tooltip title="Download link unavailable">
            {/* Wrapper span keeps the Tooltip listener active while the Button is disabled. */}
            <span className="inline-block flex-none">
              {/* Disabled keeps antd's own greyed treatment — the point is that it reads as unavailable. */}
              <Button
                shape="circle"
                icon={<DownloadOutlined />}
                aria-label="Download"
                disabled
                className={DOWNLOAD_BUTTON_CLASSES}
              />
            </span>
          </Tooltip>
        ))}
    </div>
  );
}

/**
 * Popover body for the Smart Calendar "Report(s)" column: a compact, scrollable list
 * of a deal's deliverable reports. Purely presentational — the popover shell, the
 * `GET /deals/:dealId/deliverables` fetch, and the viewer modal all live in the host
 * (Task 4); this component only renders `reports` and forwards clicks via `onOpen`.
 * By design there is no add/create action here — new work is requested from the
 * Deals surface, not from this popover.
 * @param {CalendarReportsListProps} props - The reports, header count override, loading state, and open handler.
 * @returns {React.ReactElement} The popover body.
 */
export function CalendarReportsList({
  reports,
  loading,
  onOpen,
  headerCount,
  maxRows = 3
}: CalendarReportsListProps): React.ReactElement {
  // Tailwind's JIT can't generate a class from this runtime-computed value, so the
  // scroll container's max-height is the one permitted inline style in this file.
  const maxHeight = maxRows * ROW_HEIGHT_PX + LIST_PADDING_Y_PX;

  return (
    <div className="w-[388px]">
      {/*
       * Navy fill rather than a hairline-under-grey-caps: it is the same treatment the calendar's
       * own table header carries, so the popover reads as a fragment of the grid it sprang from
       * instead of a floating panel. The overlay clips to `rounded-lg`, so the fill takes the
       * popover's top corners. The old bottom rule is gone — the fill is the boundary now.
       */}
      <div className="px-[18px] py-[16px] bg-[#152D51] text-[13px] leading-[20px] font-bold tracking-[.09em] uppercase text-white">
        {headerCount ?? reports.length} Report(s)
      </div>
      <div
        className="py-[6px] overflow-y-auto [&::-webkit-scrollbar]:w-[6px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#C9D2DE]"
        style={{ maxHeight }}
      >
        {loading ? (
          <div className="px-[16px]">
            <Skeleton active paragraph={{ rows: 2 }} />
          </div>
        ) : reports.length === 0 ? (
          <div className="px-[16px] py-[10px] text-[12px] text-[#777]">No reports yet</div>
        ) : (
          reports.map((report) => <ReportRow key={report.deliverableId} report={report} onOpen={onOpen} />)
        )}
      </div>
    </div>
  );
}
